import type {
  Conversation,
  ConversationMessage,
} from "@mirage/service-conversation/types";
import type { ComposeAssistantConversationMessage } from "@mirage/shared/compose/assist-message";
import { useMemo } from "react";

interface UseFilteredMessagesResult {
  messagesToDisplay: ConversationMessage[];
  templateSelectedMessage?: ComposeAssistantConversationMessage;
}

// Enum identifying different types of special cases
enum SpecialCaseType {
  TEMPLATE_MESSAGE = "TEMPLATE_MESSAGE",
  WORKFLOW_PROMPT = "WORKFLOW_PROMPT",
}

// Interface defining the structure of a special case handler
interface SpecialCaseHandler {
  type: SpecialCaseType;
  // Function that checks if a message matches this handler's pattern
  matchesPattern: (message: ConversationMessage) => boolean;
  handle: (
    message: ConversationMessage,
    remainingMessages: ConversationMessage[],
  ) => UseFilteredMessagesResult;
  // Function to check if all prerequisites are met for this special case
  prerequisites?: (params: { agentId?: string }) => boolean;
}

/**
 * Special case handlers for different types of messages that need special processing.
 * Each handler consists of:
 * - type: A unique identifier for the special case
 * - matchesPattern: A function that checks if a message matches this handler's pattern
 * - handle: A function that processes the message when it matches the pattern
 * - prerequisites: Optional function to check if all requirements are met for this case
 *
 * The order of handlers matters - the first matching handler will be used.
 */
const specialCases: SpecialCaseHandler[] = [
  // Template message handler: Messages with llmIgnore=true are treated as template selections
  {
    type: SpecialCaseType.TEMPLATE_MESSAGE,
    matchesPattern: (message) =>
      message.messageType?.case === "userMessage" &&
      message.messageType.value?.llmIgnore === true,
    handle: (message, remainingMessages) => {
      // Convert template message to the format expected by ContextInputMessages
      const composeMessage = {
        type: "message",
        role: "user",
        text: message.messageType.value?.text || "",
        actionContext: { type: "template_selected" },
      } as ComposeAssistantConversationMessage;
      return {
        messagesToDisplay: remainingMessages,
        templateSelectedMessage: composeMessage,
      };
    },
  },
  // Workflow prompt handler: Messages starting with "SYSTEM_PROMPT:" and having an agentId
  {
    type: SpecialCaseType.WORKFLOW_PROMPT,
    matchesPattern: (message) =>
      message.messageType.case === "userMessage" &&
      message.messageType.value.text.startsWith("SYSTEM_PROMPT:"),
    prerequisites: ({ agentId }) => agentId !== undefined,
    handle: (_, remainingMessages) => ({
      messagesToDisplay: remainingMessages,
    }),
  },
  // Add new special cases here following the same pattern:
  // {
  //   type: SpecialCaseType.NEW_CASE,
  //   matchesPattern: (message) => /* check if message matches pattern */,
  //   prerequisites: (params) => /* check prerequisites */,
  //   handle: (message, remainingMessages) => /* handle special case */,
  // },
];

/**
 * A hook that filters and processes conversation messages to handle special cases like template messages
 * and workflow prompts. This hook is used to prepare messages for display in the conversation UI.
 *
 * Special cases handled:
 * 1. Template Messages: Messages with llmIgnore=true are treated as template selections and are
 *    converted to a special format for display in the UI. These messages are excluded from the
 *    main message display.
 *
 * 2. Workflow Prompts: Messages starting with "SYSTEM_PROMPT:" and having an associated agentId
 *    are treated as workflow initialization messages and are excluded from the main message display.
 *
 * The hook returns:
 * - messagesToDisplay: The filtered list of messages to show in the conversation
 * - templateSelectedMessage: If a template message is detected, it's converted to a format suitable
 *   for display in the template selection UI
 *
 * @param conversation - The conversation object containing messages to process
 * @param agentId - Optional agent ID used to identify workflow prompts
 * @returns Object containing filtered messages and template message if present
 */
export function useFilteredMessages(
  conversation: Conversation | undefined,
  agentId?: string,
): UseFilteredMessagesResult {
  // Memoize the filtering logic since it depends on conversation.messages and agentId
  const result = useMemo(() => {
    // Handle empty conversation case
    if (!conversation?.messages?.length) {
      return { messagesToDisplay: [], templateSelectedMessage: undefined };
    }

    const firstMessage = conversation.messages[0];
    const remainingMessages = conversation.messages.slice(1);

    // Find the first matching special case handler
    const matchingCase = specialCases.find((specialCase) => {
      // Check if all prerequisites are met
      if (
        specialCase.prerequisites &&
        !specialCase.prerequisites({ agentId })
      ) {
        return false;
      }
      return specialCase.matchesPattern(firstMessage);
    });

    // Apply the matching handler if found, otherwise return all messages
    if (matchingCase) {
      return matchingCase.handle(firstMessage, remainingMessages);
    }

    // Default case: no special handling needed, return all messages
    return {
      messagesToDisplay: conversation.messages,
      templateSelectedMessage: undefined,
    };
  }, [conversation?.messages, agentId]);

  return result;
}
